// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SubscriptionPopupDesinger.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { transaction } from 'mobx';
import connect from 'share/enhancers/Izenda.Connect';
import fetcher from 'fetchers/Izenda.SystemSettingFetcher';
import uiState from 'logic/Izenda.UiState';

import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import { Tabs, Tab } from 'share/components/controls/Izenda.Tab';
import Schedule from './Izenda.Schedule';
import Delivery from './Izenda.Delivery';
import Panel from 'share/components/controls/Izenda.Panel';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';

import ReportSubscriptionConstants from 'logic/data/Izenda.ReportSubscriptionConstants';
import { EMAIL_VALIDATION } from 'logic/data/Izenda.EmailConstants';
import SubscriptionDetails from 'logic/models/Izenda.SubscriptionDetails';
import InstanceName from './Izenda.InstanceName';
import MobxUtils from 'share/utils/Izenda.MobxUtils';
import { cloneDeep, forEach, findIndex, split, map, isEmpty, head, get } from 'lodash/fp';

const CANCEL_TEXT = 'cancel';
const ACCEPT_TEXT = 'accept';
const NUMBER_CHECK_INDEX = -1;
@connect()
export default class SubscriptionPopup extends Component {
  static propTypes = {
    data: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.cloneData();
  }

  componentWillReceiveProps() {
    this.cloneData();
  }

  /**
   * Clone data object
   */
  cloneData() {
    const { data } = this.props;
    transaction(() => {
      this.subscriptionData = new SubscriptionDetails(cloneDeep(data), data.emailTemplates, data.reportFilter);
      this.subscriptionData.updateProperty(
        'subscriptionFilterFields',
        data.subscriptionFilterFields && cloneDeep(MobxUtils.toJS(data.subscriptionFilterFields))
      );
      this.subscriptionData.updateProperty(
        'subscriptionCommonFilterFields',
        data.subscriptionCommonFilterFields && cloneDeep(MobxUtils.toJS(data.subscriptionCommonFilterFields))
      );
      this.subscriptionData.cleanDirty();
    });
  }

  /**
   * Get title
   */
  getTitle() {
    const { localization } = this.props;
    const { isSubscription, isEdit } = this.subscriptionData;
    if (isSubscription)
      return isEdit
        ? localization.getText('DASHBOARD_SUBSCRIPTION_CONSTANTS_PAGE_TEXT_EDIT')
        : localization.getText('DASHBOARD_SUBSCRIPTION_CONSTANTS_PAGE_TEXT_ADD');
    return isEdit
      ? localization.getText('DASHBOARD_SCHEDULING_CONSTANTS_PAGE_TEXT_EDIT')
      : localization.getText('DASHBOARD_SCHEDULING_CONSTANTS_PAGE_TEXT_ADD');
  }

  /**
   * Validate Occurrence value.
   * @return {Promise}
   */
  validateOccurrence() {
    const { validation } = this.props;

    if (this.subscriptionData.isEndAfter) {
      const tempValue = parseInt(this.subscriptionData.occurrence, 10);

      if (!tempValue) {
        validation.updateErrors([
          {
            key: 'occurrence',
            messages: [ReportSubscriptionConstants.MESSAGE.requiredOccurrence]
          }
        ]);
        return Promise.resolve(false);
      }

      this.subscriptionData.occurrence = tempValue;
    }

    return Promise.resolve(true);
  }

  /**
   * Validate subscription instance.
   * @param {Boolean} forSaving
   * @return {Promise}
   */
  validateSubscription(forSaving: Boolean) {
    const { validation } = this.props;
    return validation.validate().then(success => {
      if (success) {
        return this.validateOccurrence().then(result => {
          if (result) {
            if (forSaving) {
              return this.checkDuplicateInstanceName(forSaving);
            }
            return Promise.resolve(true);
          }
          return Promise.resolve(false);
        });
      }

      return success;
    });
  }

  /**
   * Check Duplicate instance name.
   * We check on browser first, then check with api if pass.
   * @return {Promise}
   */
  checkDuplicateInstanceName(forSaving) {
    const { validation, dashboardDetail } = this.props;
    const commonFilterFields = get('dashboard.commonFilterFields', dashboardDetail);
    if (!this.props.onCheckDuplicateInstanceName(this.subscriptionData)) {
      const newCommonFilterFields = dashboardDetail && commonFilterFields.toJSON();

      return this.props.onValidateSubscription(this.subscriptionData, newCommonFilterFields).then(result => {
        if (result.success) {
          const originalId = this.subscriptionData.id;
          forSaving && this.subscriptionData.buildDataForSaving();
          this.subscriptionData.populate(
            result.subscription,
            this.subscriptionData.emailTemplates,
            this.subscriptionData.reportFilter
          );
          const subscription = get('subscription', result);
          if (subscription && subscription.state === ObjectStateConstants.INSERT) {
            this.subscriptionData.id = originalId;
          }
          this.subscriptionData.markDirty();

          if (result.subscription.isStartDateAdjusted && forSaving) {
            return this.props.confirmMessage('warning', ReportSubscriptionConstants.MESSAGE.M06).then(function() {
              return Promise.resolve(false);
            });
          }
        }

        if (!result.success) {
          const errorKeys = map(item => {
            return item.key;
          }, result.messages);

          this.handleRedirectTabWhenError(errorKeys);

          validation.updateErrors(result.messages);
          return Promise.resolve(!forSaving);
        }

        validation.updateErrors(result.messages);
        return Promise.resolve(result.success);
      });
    } else {
      validation.updateErrors([
        {
          key: 'name',
          messages: [ReportSubscriptionConstants.MESSAGE.M05]
        }
      ]);
      return Promise.resolve(false);
    }
  }

  /**
   * Handle redirect among tabs when error occurs
   */
  handleRedirectTabWhenError(errorKeys) {
    const {
      tabs: {
        props: { activeTabKey }
      }
    } = this.refs;
    // If there is only error of 'Recipients', then move to 'Delivery' tab.
    if (
      this.isAllErrorInSet([ReportSubscriptionConstants.FIELD_KEY.Recipients], errorKeys) &&
      activeTabKey !== ReportSubscriptionConstants.DELIVERY_TAB.key
    ) {
      this.setActiveTab(ReportSubscriptionConstants.DELIVERY_TAB.key);
    } else if (
      this.isAllErrorInSet(
        [
          ReportSubscriptionConstants.FIELD_KEY.StartDate,
          ReportSubscriptionConstants.FIELD_KEY.StartTime,
          ReportSubscriptionConstants.FIELD_KEY.EndDate
        ],
        errorKeys
      ) &&
      activeTabKey !== ReportSubscriptionConstants.SCHEDULER_TAB.key
    ) {
      this.setActiveTab(ReportSubscriptionConstants.SCHEDULER_TAB.key);
    }
  }

  /**
   * Handle redirect among tabs when error occurs
   */
  isAllErrorInSet(set, errorKeys) {
    let result = true;
    forEach(key => {
      const index = findIndex(item => {
        return item === key;
      }, set);

      if (index === NUMBER_CHECK_INDEX) {
        result = false;
        return false;
      }
    }, errorKeys);
    return result;
  }

  /**
   * Set active tab
   * @param  {String} value
   */
  setActiveTab(value: String) {
    this.subscriptionData.setCurrentTab(value);
  }

  /**
   * When click button OK on popup
   * @return {Promise} promise to handle async action
   */
  onAcceptClosing() {
    const {
      props,
      props: { localization, validation }
    } = this;
    const subscriptionData = this.subscriptionData;

    fetcher.isHavemailConfiguration(uiState.tenantId).then(result => {
      if (!result.success && subscriptionData.deliveryType === head(ReportSubscriptionConstants.DELIVERY_TYPE).value) {
        return props.confirmMessage('error', result.message);
      }
      this.validateSubscription(true).then(result => {
        if (result && this.validateEmailFormat()) {
          props.onSave(subscriptionData);
          validation.reset();
          props.onAccept(subscriptionData);
        }
        return result;
      });
    });
  }

  /**
   * Validate email format recipients
   * @return {Boolean}
   */
  validateEmailFormat(): Boolean {
    const { validation } = this.props;
    const emails = split(';', this.subscriptionData.recipients);
    const invalidEmails = [];
    forEach( email => {
      if (email && !EMAIL_VALIDATION.REGEX.test(email)) {
        invalidEmails.push(email);
      }
    }, emails);

    if (isEmpty(invalidEmails)) return true;

    validation.updateErrors([
      {
        key: ReportSubscriptionConstants.FIELD_KEY.Recipients,
        messages: [EMAIL_VALIDATION.MESSAGE(invalidEmails)]
      }
    ]);

    return false;
  }

  /**
   * When click button Cancel on popup
   * @param  {Object} hidePopup function
   */
  onCancelClosing() {
    this.props.validation.reset();
    this.props.onReject();
  }

  /**
   * Closing popup
   * @param {text} type
   */
  onClosing = (closeCallback, type) => {
    switch (type) {
      case ACCEPT_TEXT:
        this.onAcceptClosing();
        break;
      case CANCEL_TEXT:
        this.onCancelClosing();
        break;
    }
  };

  /**
   * Render component
   */
  render() {
    const data = this.subscriptionData;
    const { localization } = this.props;

    const canChangeTab = (index, tabPanel, tabMenu, activeTabKey) => {
      return this.validateSubscription().then(result => {
        if (result) {
          this.subscriptionData.setCurrentTab(activeTabKey);
        }
        return false;
      });
    };

    return (
      <Popup visible onClosing={this.onClosing} autoClose={false} type='none' sizeClass='modal-xs'>
        <Popup.Header title={this.getTitle()} />
        <Popup.Body>
          <div className='subscription'>
            <div className='form-horizontal'>
              <InstanceName data={data} />
              <hr className='izenda-full-hr' />
              <Panel className='panel-header-custom'>
                <Tabs ref='tabs' activeTabKey={data.currentTab} className='left-tab' canChangeTab={canChangeTab}>
                  <Tab
                    title={localization.getText('SUBSCRIPTION_LABEL_SCHEDULE')}
                    tabKey={ReportSubscriptionConstants.SCHEDULER_TAB.key}
                  >
                    <Schedule data={data} onValidateSubscription={this.props.onValidateSubscription} />
                  </Tab>
                  <Tab
                    title={localization.getText('SUBSCRIPTION_LABEL_DELIVERY')}
                    tabKey={ReportSubscriptionConstants.DELIVERY_TAB.key}
                  >
                    <Delivery {...this.props} data={data} />
                  </Tab>
                </Tabs>
              </Panel>
            </div>
          </div>
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type='accept'>{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type='cancel'>
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}


// WEBPACK FOOTER //
// ./ui/modules/Subscription/Izenda.SubscriptionPopupDesigner.jsx